import React from "react";
import { Navigate, useNavigationType } from "react-router-dom";
import { readBinding, saveBinding, isBound } from "./utils/storeBinding";

const HOUR = 60 * 60 * 1000;

/* ---------------------- small utils ---------------------- */
function readJSON(key) {
  try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
}
function writeJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

function relinkIsRequired() {
  try {
    return localStorage.getItem("neo.relinkRequired") === "1";
  } catch {
    return false;
  }
}

/* ---------------------- binding helpers ---------------------- */
/** Return any org/store binding we can find from LS or sessions. */
function getBinding() {
  // A manager-authorised reset is a hard security boundary.
  // Do not heal the just-cleared device from stale sessions/legacy keys.
  if (relinkIsRequired()) return { orgId: "", storeId: "", storeName: "" };

  // direct keys written by InstallGate, healed from local/session/cookie first
  const binding = readBinding();
  let orgId = binding.orgId || localStorage.getItem("orgId") || "";
  let storeId = binding.storeId || localStorage.getItem("storeId") || "";
  let storeName = binding.storeName || localStorage.getItem("storeName") || "";

  // try staff session
  const ss = readJSON("cashup_staff_session") || readJSON("staffSession") || readJSON("session_staff") || readJSON("cashup.session");
  if (!orgId   && ss?.orgId)    orgId = String(ss.orgId);
  if (!storeId && ss?.storeId)  storeId = String(ss.storeId);
  if (!storeName && ss?.storeName) storeName = String(ss.storeName || "");

  // try manager session
  const ms = readJSON("managerSession") || readJSON("cashup_manager_session") || readJSON("session_manager");
  if (!orgId   && ms?.orgId)    orgId = String(ms.orgId);
  if (!storeId && ms?.storeId)  storeId = String(ms.storeId);
  if (!storeName && ms?.storeName) storeName = String(ms.storeName || "");

  // legacy keys some screens still read
  if (!storeId)   storeId   = localStorage.getItem("managerStoreId")  || storeId;
  if (!storeName) storeName = localStorage.getItem("managerStoreName")|| storeName;

  return { orgId, storeId, storeName };
}

/** If LS is missing pieces but a session has them, copy into LS. */
function healLocalBinding() {
  const { orgId, storeId, storeName } = getBinding();
  if (orgId && storeId) saveBinding({ orgId, storeId, storeName });
}

/** Treat device as linked if we at least know the storeId. */
function hasStoreBinding() {
  if (relinkIsRequired()) return false;
  // IMPORTANT: keep render-time guard checks pure.
  // healLocalBinding() calls saveBinding(), which dispatches
  // `neo:binding-changed`; that can update mounted kiosk hooks while
  // RequireStoreLink is still rendering and triggers React's
  // "Cannot update a component while rendering a different component" warning.
  if (isBound()) return true;
  const { orgId, storeId } = getBinding();
  return !!(orgId && storeId);
}

/* ---------------------- STAFF ---------------------- */
export function getStaffSession() {
  // preferred JSON session keys
  const candidates = [
    "cashup_staff_session",
    "staffSession",
    "session_staff",
    "cashup.session",
  ];
  for (const k of candidates) {
    const v = readJSON(k);
    if (v && v.type === "staff" && v.storeId) return v;
  }

  // Legacy fallback (primitives)
  const name = localStorage.getItem("staffName");
  const role = localStorage.getItem("staffRole") || "";
  const { storeId, storeName } = getBinding(); // pulls from LS or sessions
  if (name && storeId) {
    const now = Date.now();
    const sess = {
      type: "staff",
      name, role, storeId, storeName: storeName || "",
      startedAt: now,
      expiresAt: now + 16 * HOUR,
    };
    writeJSON("cashup_staff_session", sess);
    healLocalBinding();
    return sess;
  }
  return null;
}

export function isStaffSessionValid(sess) {
  if (!sess) return false;
  if (!sess.name || !sess.storeId) return false;
  if (sess.expiresAt && Date.now() > Number(sess.expiresAt)) return false;
  return true;
}

/** Guard only checks for binding + staff session (no longer requires storeName). */
export function RequireStoreLink({ children }) {
  const linked = hasStoreBinding();

  // Heal legacy/session-derived binding after commit, never during render.
  React.useEffect(() => {
    if (linked) healLocalBinding();
  }, [linked]);

  return linked ? children : <Navigate to="/link" replace />;
}

export function RequireStaffSession({ children }) {
  const disabled = String(process.env.REACT_APP_DISABLE_STAFF_GUARD || "").toLowerCase() === "true";
  if (disabled) return children;
  const sess = getStaffSession();
  return isStaffSessionValid(sess) ? children : <Navigate to="/" replace />;
}

/* --------------------- MANAGER --------------------- */
export function getManagerSession() {
  const candidates = [
    "managerSession",
    "cashup_manager_session",
    "session_manager",
  ];
  for (const k of candidates) {
    const v = readJSON(k);
    if (v && v.type === "manager" && v.storeId) return v;
  }

  // Legacy primitives → normalize
  const name = localStorage.getItem("managerName");
  const { storeId, storeName } = getBinding();
  if (name && storeId) {
    const now = Date.now();
    const sess = {
      type: "manager",
      name,
      storeId,
      storeName: storeName || "",
      issuedAt: now,
      expiresAt: now + 12 * HOUR,
    };
    writeJSON("managerSession", sess);
    healLocalBinding();
    return sess;
  }
  return null;
}

export function isManagerSessionValid(sess) {
  if (!sess) return false;
  if (!sess.name || !sess.storeId) return false;
  if (sess.expiresAt && Date.now() > Number(sess.expiresAt)) {
    try { localStorage.removeItem("managerSession"); } catch {}
    return false;
  }
  return true;
}

export function RequireManagerSession({ children }) {
  const disabled = String(process.env.REACT_APP_DISABLE_MANAGER_GUARD || "").toLowerCase() === "true";
  if (disabled) return children;
  const sess = getManagerSession();
  return isManagerSessionValid(sess) ? children : <Navigate to="/manager-login" replace />;
}

/**
 * Block browser back/forward (POP) navigation into protected pages.
 * Useful for iPad swipe / mouse back-forward where we want a fresh PIN.
 */
export function RequireNoPop({ children, redirectTo = "/" }) {
  const navType = useNavigationType();
  if (navType === "POP") return <Navigate to={redirectTo} replace />;
  return children;
}
